import {LucideIcon, formatNumber} from '@tryghost/shade/utils';
import {OverridesPanel} from '@/components/admin/OverridesPanel';
import {Select} from '@/components/ui/Select';
import {useAdminUserDetail, useAdminUsers} from '@/hooks/useAdminUsers';
import {useAuth} from '@/contexts/AuthContext';
import {useDebounce} from '@/hooks/useDebounce';
import {useEffect, useState} from 'react';
import {useMediaQuery} from '@/hooks/useMediaQuery';
import type {User} from '@/types/api';

function RoleBadge({role}: { role: string }) {
    const isSuperadmin = role === 'superadmin';
    const isStaff = role === 'staff';
    return (
        <span
            style={{
                display: 'inline-block',
                fontSize: '1.1rem',
                fontWeight: 600,
                padding: '2px 10px',
                borderRadius: 40,
                textTransform: 'uppercase',
                letterSpacing: '0.4px',
                background: isSuperadmin ? 'rgba(239, 90, 43, 0.1)' : isStaff ? 'rgba(59, 130, 246, 0.1)' : 'var(--tt-badge-bg)',
                color: isSuperadmin ? 'var(--tt-accent)' : isStaff ? '#3B82F6' : 'var(--tt-text-secondary)'
            }}
        >
            {role}
        </span>
    );
}

function StatusBadge({status}: { status: string }) {
    const colors: Record<string, { bg: string; color: string }> = {
        active: {bg: 'var(--studio-soft)', color: 'var(--tt-text-secondary)'},
        suspended: {bg: 'rgba(245, 11, 35, 0.1)', color: 'var(--tt-red)'}
    };
    const c = colors[status] || {bg: 'var(--tt-badge-bg)', color: 'var(--tt-text-secondary)'};
    return (
        <span
            style={{
                display: 'inline-block',
                fontSize: '1.1rem',
                fontWeight: 600,
                padding: '2px 10px',
                borderRadius: 40,
                textTransform: 'uppercase',
                letterSpacing: '0.4px',
                background: c.bg,
                color: c.color
            }}
        >
            {status}
        </span>
    );
}

function UserDetailModal({userId, onClose}: { userId: string; onClose: () => void }) {
    const {data: user, isLoading, isError} = useAdminUserDetail(userId);

    return (
        <div
            style={{
                position: 'fixed',
                inset: 0,
                zIndex: 9999,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                padding: 24
            }}
        >
            <div
                style={{
                    position: 'absolute',
                    inset: 0,
                    background: 'rgba(0, 0, 0, 0.6)',
                    backdropFilter: 'blur(4px)'
                }}
                onClick={onClose}
            />
            <div className="studio-panel"
                style={{position: 'relative', background: 'var(--tt-card-bg)', border: '1px solid var(--tt-card-border)', borderRadius: 14, width: '100%', maxWidth: 640, maxHeight: '85vh', overflow: 'auto', padding: 0}}
            >
                <div style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    padding: '20px 24px',
                    borderBottom: '1px solid var(--tt-card-border)'
                }}>
                    <h2 style={{fontSize: '1.8rem', fontWeight: 500, color: 'var(--tt-text-primary)', margin: 0, letterSpacing: '-.025em'}}>
                        User Details
                    </h2>
                    <button style={{
                        background: 'none',
                        border: 'none',
                        fontSize: '2rem',
                        color: 'var(--tt-text-secondary)',
                        cursor: 'pointer',
                        padding: '0 4px',
                        lineHeight: 1
                    }}
                    type="button"
                    onClick={onClose}
                    >
                        &times;
                    </button>
                </div>

                <div style={{padding: '20px 24px'}}>
                    {isLoading && (
                        <p style={{fontSize: '1.4rem', color: 'var(--tt-text-secondary)', textAlign: 'center', padding: 32}}>
                            Loading...
                        </p>
                    )}

                    {isError && (
                        <p style={{fontSize: '1.4rem', color: 'var(--tt-red)', textAlign: 'center', padding: 32}}>
                            Failed to load user details
                        </p>
                    )}

                    {user && (
                        <div style={{display: 'flex', flexDirection: 'column', gap: 24}}>
                            <div style={{
                                display: 'grid',
                                gridTemplateColumns: '1fr 1fr',
                                gap: '16px 24px'
                            }}>
                                <DetailField label="Name" value={user.name || 'Unnamed'} />
                                <DetailField label="Email" value={user.email} />
                                <DetailField label="Role" value={<RoleBadge role={user.role} />} />
                                <DetailField label="Status" value={<StatusBadge status={user.status} />} />
                                <DetailField label="Email Verified" value={user.email_verified_at ? new Date(user.email_verified_at).toLocaleDateString() : 'No'} />
                                <DetailField label="Joined" value={new Date(user.created_at).toLocaleDateString()} />
                            </div>

                            <OverridesPanel key={userId} scope="users" subjectId={userId} />
                            {user.ownedSites.length > 0 && (
                                <div>
                                    <h3 style={{fontSize: '1.4rem', fontWeight: 500, color: 'var(--tt-text-primary)', margin: '0 0 12px 0', letterSpacing: '-.025em'}}>
                                        Owned Newsletters ({formatNumber(user.ownedSites.length)})
                                    </h3>
                                    <div style={{
                                        background: 'var(--tt-bg)',
                                        border: '1px solid var(--tt-card-border)',
                                        borderRadius: 8,
                                        overflow: 'hidden'
                                    }}>
                                        {user.ownedSites.map(site => (
                                            <div
                                                key={site.id}
                                                style={{
                                                    display: 'flex',
                                                    justifyContent: 'space-between',
                                                    alignItems: 'center',
                                                    padding: '10px 16px',
                                                    borderBottom: '1px solid var(--tt-divider)'
                                                }}
                                            >
                                                <div>
                                                    <div style={{fontSize: '1.3rem', fontWeight: 500, color: 'var(--tt-text-primary)'}}>
                                                        {site.name}
                                                    </div>
                                                    <div style={{fontSize: '1.2rem', color: 'var(--tt-text-secondary)'}}>
                                                        {site.slug}
                                                    </div>
                                                </div>
                                                <StatusBadge status={site.status} />
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            )}

                            {user.teamMemberships.length > 0 && (
                                <div>
                                    <h3 style={{fontSize: '1.4rem', fontWeight: 500, color: 'var(--tt-text-primary)', margin: '0 0 12px 0', letterSpacing: '-.025em'}}>
                                        Team Memberships ({formatNumber(user.teamMemberships.length)})
                                    </h3>
                                    <div style={{display: 'flex', flexDirection: 'column', gap: 12}}>
                                        {user.teamMemberships.map(membership => (
                                            <div
                                                key={membership.id}
                                                style={{
                                                    background: 'var(--tt-bg)',
                                                    border: '1px solid var(--tt-card-border)',
                                                    borderRadius: 8,
                                                    padding: 16
                                                }}
                                            >
                                                <div style={{display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8}}>
                                                    <div>
                                                        <span style={{fontSize: '1.3rem', fontWeight: 500, color: 'var(--tt-text-primary)'}}>
                                                            Owner: {membership.owner_name || membership.owner_email}
                                                        </span>
                                                    </div>
                                                    <div style={{display: 'flex', gap: 8}}>
                                                        <RoleBadge role={membership.role} />
                                                        <StatusBadge status={membership.status} />
                                                    </div>
                                                </div>
                                                {membership.sites.length > 0 && (
                                                    <div style={{marginTop: 8}}>
                                                        <div style={{fontSize: '1.1rem', fontWeight: 600, color: 'var(--tt-text-secondary)', textTransform: 'uppercase', letterSpacing: '0.4px', marginBottom: 6}}>
                                                            Newsletters
                                                        </div>
                                                        {membership.sites.map(site => (
                                                            <div
                                                                key={site.tenant_id}
                                                                style={{
                                                                    display: 'flex',
                                                                    justifyContent: 'space-between',
                                                                    alignItems: 'center',
                                                                    padding: '6px 0',
                                                                    borderTop: '1px solid var(--tt-divider)'
                                                                }}
                                                            >
                                                                <span style={{fontSize: '1.2rem', color: 'var(--tt-text-primary)'}}>
                                                                    {site.site_name} <span style={{color: 'var(--tt-text-secondary)'}}>({site.site_slug})</span>
                                                                </span>
                                                                <span style={{
                                                                    fontSize: '1.0rem',
                                                                    fontWeight: 600,
                                                                    padding: '1px 8px',
                                                                    borderRadius: 40,
                                                                    textTransform: 'uppercase',
                                                                    background: site.sync_status === 'synced' ? 'var(--studio-soft)' : site.sync_status === 'failed' ? 'rgba(245, 11, 35, 0.1)' : 'var(--tt-badge-bg)',
                                                                    color: site.sync_status === 'synced' ? 'var(--tt-text-secondary)' : site.sync_status === 'failed' ? 'var(--tt-red)' : 'var(--tt-text-secondary)'
                                                                }}>
                                                                    {site.sync_status}
                                                                </span>
                                                            </div>
                                                        ))}
                                                    </div>
                                                )}
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            )}

                            {user.ownedSites.length === 0 && user.teamMemberships.length === 0 && (
                                <p style={{fontSize: '1.3rem', color: 'var(--tt-text-secondary)', textAlign: 'center', padding: 16}}>
                                    No newsletters or team memberships found for this user.
                                </p>
                            )}
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
}

function DetailField({label, value}: { label: string; value: React.ReactNode }) {
    return (
        <div>
            <div style={{fontSize: '1.1rem', fontWeight: 600, color: 'var(--tt-text-secondary)', textTransform: 'uppercase', letterSpacing: '0.4px', marginBottom: 4}}>
                {label}
            </div>
            <div style={{fontSize: '1.4rem', color: 'var(--tt-text-primary)'}}>
                {value}
            </div>
        </div>
    );
}

const ROLE_OPTIONS = [
    {value: '', label: 'All roles'},
    {value: 'superadmin', label: 'Superadmin'},
    {value: 'admin', label: 'Admin'},
    {value: 'staff', label: 'Staff'}
];

export function AdminUsersPage() {
    const {user: currentUser} = useAuth();
    const {isMobile, isTablet} = useMediaQuery();
    const isCompact = isMobile || isTablet;
    const [page, setPage] = useState(1);
    const [searchInput, setSearchInput] = useState('');
    const debouncedSearch = useDebounce(searchInput, 300);
    const [roleFilter, setRoleFilter] = useState('');
    const [selectedUserId, setSelectedUserId] = useState<string | null>(null);

    const {data, isLoading, isError, error: queryError} = useAdminUsers(page, debouncedSearch, roleFilter);

    const users = data?.data || [];
    const totalPages = data?.pagination?.totalPages || 1;
    const total = data?.pagination?.total || 0;

    useEffect(() => {
        setPage(1);
    }, [debouncedSearch, roleFilter]);

    const handleUserClick = (user: User) => {
        setSelectedUserId(user.id);
    };

    return (
        <div className="studio-content-page studio-admin-page studio-admin-directory-page">
            {selectedUserId && (
                <UserDetailModal
                    userId={selectedUserId}
                    onClose={() => setSelectedUserId(null)}
                />
            )}

            <header className="studio-page-heading studio-admin-page-heading">
                <div>
                    <p className="studio-eyebrow">Access directory</p>
                    <h1>Platform users</h1>
                    <p className="studio-admin-intro">Review accounts, roles, status, and publication access across TribeTale.</p>
                </div>
                {!isLoading && <span className="studio-admin-heading-count"><LucideIcon.UsersRound size={16} /><strong>{formatNumber(total)}</strong>{total === 1 ? 'user' : 'users'}</span>}
            </header>

            <div className="studio-admin-toolbar">
                <label className="studio-admin-search">
                    <LucideIcon.Search size={16} />
                    <input aria-label="Search platform users"
                        className="studio-field-control"
                        placeholder="Search by name or email…"
                        type="text"
                        value={searchInput}
                        onChange={e => setSearchInput(e.target.value)}
                    />
                </label>
                <div className="studio-admin-toolbar-actions">
                    {(searchInput || roleFilter) && (
                        <button className="studio-control studio-admin-clear-filter"
                            type="button"
                            onClick={() => {
                                setSearchInput('');
                                setRoleFilter('');
                            }}
                        >
                                Clear
                        </button>
                    )}

                    <Select
                        ariaLabel="Filter users by role"
                        options={ROLE_OPTIONS}
                        placeholder="All roles"
                        value={roleFilter}
                        onChange={setRoleFilter}
                    />
                </div>
            </div>

            {isError && (
                <div className="studio-admin-error" role="alert">
                    {queryError instanceof Error ? queryError.message : 'Failed to load users'}
                </div>
            )}

            <div className="studio-panel studio-admin-data-panel"
                style={{background: 'var(--tt-card-bg)', border: '1px solid var(--tt-card-border)', borderRadius: 14, overflow: 'hidden', width: '100%'}}
            >
                {!isCompact && (
                    <div className="studio-admin-data-header"
                        style={{
                            display: 'grid',
                            gridTemplateColumns: '2fr 1fr 1fr',
                            gap: 16,
                            padding: '12px 24px',
                            borderBottom: '1px solid var(--tt-card-border)',
                            background: 'var(--tt-table-header-bg)'
                        }}
                    >
                        <span style={{fontSize: '1.1rem', fontWeight: 600, color: 'var(--tt-text-secondary)', textTransform: 'uppercase', letterSpacing: '0.4px'}}>User</span>
                        <span style={{fontSize: '1.1rem', fontWeight: 600, color: 'var(--tt-text-secondary)', textTransform: 'uppercase', letterSpacing: '0.4px'}}>Role</span>
                        <span style={{fontSize: '1.1rem', fontWeight: 600, color: 'var(--tt-text-secondary)', textTransform: 'uppercase', letterSpacing: '0.4px'}}>Status</span>
                    </div>
                )}

                {isLoading ? (
                    <div style={{padding: isCompact ? '32px 16px' : '48px 24px', textAlign: 'center'}}>
                        <p style={{fontSize: '1.4rem', color: 'var(--tt-text-secondary)'}}>Loading users...</p>
                    </div>
                ) : users.length === 0 ? (
                    <div style={{padding: isCompact ? '32px 16px' : '48px 24px', textAlign: 'center'}}>
                        <p style={{fontSize: '1.4rem', color: 'var(--tt-text-secondary)'}}>
                            {debouncedSearch || roleFilter ? 'No users match your filters' : 'No users found'}
                        </p>
                    </div>
                ) : isCompact ? (
                    users.map(user => (
                        <div key={user.id}
                            className="studio-admin-data-row is-mobile"
                            style={{
                                padding: '14px 16px',
                                borderBottom: '1px solid var(--tt-divider)',
                                cursor: 'pointer'
                            }}
                            onClick={() => handleUserClick(user)}
                        >
                            <div style={{display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 8}}>
                                <div style={{flex: 1, minWidth: 0}}>
                                    <div style={{fontSize: '1.4rem', fontWeight: 500, color: 'var(--tt-text-primary)'}}>
                                        {user.name || 'Unnamed'}
                                        {user.id === currentUser?.id && (
                                            <span style={{fontSize: '1.1rem', color: 'var(--tt-text-secondary)', fontWeight: 400, marginLeft: 6}}>(you)</span>
                                        )}
                                    </div>
                                    <div style={{fontSize: '1.2rem', color: 'var(--tt-text-secondary)', marginTop: 2, wordBreak: 'break-all'}}>{user.email}</div>
                                </div>
                                <StatusBadge status={user.status} />
                            </div>
                            <div>
                                <RoleBadge role={user.role} />
                            </div>
                        </div>
                    ))
                ) : (
                    users.map(user => (
                        <div key={user.id}
                            className="studio-admin-data-row"
                            style={{
                                display: 'grid',
                                gridTemplateColumns: '2fr 1fr 1fr',
                                gap: 16,
                                padding: '16px 24px',
                                alignItems: 'center',
                                borderBottom: '1px solid var(--tt-divider)',
                                transition: 'background 0.1s ease',
                                cursor: 'pointer'
                            }}
                            onClick={() => handleUserClick(user)}
                            onMouseEnter={(e) => {
                                e.currentTarget.style.background = 'var(--tt-table-header-bg)';
                            }}
                            onMouseLeave={(e) => {
                                e.currentTarget.style.background = 'transparent';
                            }}
                        >
                            <div>
                                <div style={{fontSize: '1.4rem', fontWeight: 500, color: 'var(--tt-text-primary)'}}>
                                    {user.name || 'Unnamed'}
                                    {user.id === currentUser?.id && (
                                        <span style={{fontSize: '1.1rem', color: 'var(--tt-text-secondary)', fontWeight: 400, marginLeft: 8}}>(you)</span>
                                    )}
                                </div>
                                <div style={{fontSize: '1.3rem', color: 'var(--tt-text-secondary)', marginTop: 2}}>{user.email}</div>
                            </div>

                            <div>
                                <RoleBadge role={user.role} />
                            </div>

                            <div>
                                <StatusBadge status={user.status} />
                            </div>
                        </div>
                    ))
                )}
            </div>

            {totalPages > 1 && (
                <div
                    style={{
                        display: 'flex',
                        justifyContent: 'center',
                        alignItems: 'center',
                        gap: isCompact ? 12 : 16,
                        marginTop: isCompact ? 16 : 24,
                        flexWrap: 'wrap'
                    }}
                >
                    <button className="studio-control" disabled={page === 1}
                        style={{height: 34, padding: '0 14px', borderRadius: 8, background: 'var(--tt-card-bg)', border: '1px solid var(--tt-card-border)', color: page === 1 ? 'var(--tt-btn-disabled-text)' : 'var(--tt-text-primary)', fontSize: '13px', fontWeight: 500, cursor: page === 1 ? 'not-allowed' : 'pointer', fontFamily: 'inherit'}}
                        type="button"
                        onClick={() => setPage(p => Math.max(1, p - 1))}
                    >
                        {isCompact ? '\u2190' : 'Previous'}
                    </button>
                    <span style={{fontSize: '1.3rem', color: 'var(--tt-text-secondary)'}}>
                        {formatNumber(page)} / {formatNumber(totalPages)}
                    </span>
                    <button className="studio-control" disabled={page === totalPages}
                        style={{height: 34, padding: '0 14px', borderRadius: 8, background: 'var(--tt-card-bg)', border: '1px solid var(--tt-card-border)', color: page === totalPages ? 'var(--tt-btn-disabled-text)' : 'var(--tt-text-primary)', fontSize: '13px', fontWeight: 500, cursor: page === totalPages ? 'not-allowed' : 'pointer', fontFamily: 'inherit'}}
                        type="button"
                        onClick={() => setPage(p => Math.min(totalPages, p + 1))}
                    >
                        {isCompact ? '\u2192' : 'Next'}
                    </button>
                </div>
            )}
        </div>
    );
}
